// Types
import { IPayPeriod, IStatus } from "@/Common/api/types/CommissionCycle";
import { resolveDisabledPositionsByStatus } from "@/Washouts/helpers/resolveDisabledPositionsByStatus";
import PositionsDialog from "../PositionsDialog";
import { TInput } from "@/Washouts/types/PositionsDialog";
import { getDisabledMessage } from "@/Washouts/helpers/PayrollClosing/checkReviewConditions";
import { useLocation } from "@/Common/hooks/Location/useLocation";
import { useCommissionCyclesData } from "@/Washouts/hooks/common/useCommissionCyclesData";
import { useMemo } from "react";

interface IRequestReviewDialogProps {
	openPositionsSelector: boolean;
	setOpenPositionsSelector: (value: boolean) => void;
	positionStatuses?: IStatus[];
	disabledPositions?: string[];
	disabledPartnerPositions?: string[];
	reviewPositions: (input: TInput) => void;
	commissionCycleId?: string;
	payPeriod?: IPayPeriod;
	partnerPayPeriod?: IPayPeriod | null;
}

const allowedStatuses = ["PENDING"];

const RequestReviewDialog = (props: IRequestReviewDialogProps) => {
	const {
		openPositionsSelector,
		setOpenPositionsSelector,
		commissionCycleId,
		payPeriod,
		partnerPayPeriod,
		positionStatuses,
		disabledPositions = [],
		disabledPartnerPositions = [],
		reviewPositions,
	} = props;

	const { timezone } = useLocation();
	const { commissionCycles } = useCommissionCyclesData();

	const disabledPositionsByStatus = resolveDisabledPositionsByStatus({
		positionStatuses,
		allowedStatuses,
	});

	const disabledPartnerPositionsByStatus = resolveDisabledPositionsByStatus({
		positionStatuses: partnerPayPeriod?.positionStatus,
		allowedStatuses,
	});

	const currentDisabledPositions = [...new Set([...disabledPositions, ...disabledPositionsByStatus])];
	const partnerDisabledPositions = [...new Set([...disabledPartnerPositions, ...disabledPartnerPositionsByStatus])];

	const handleClosePositionsSelector = () => {
		setOpenPositionsSelector(false);
	};

	const shouldDisabledCurrentPP = useMemo(() => {
		const commissionCycle = commissionCycles.find((cycle) => cycle._id === commissionCycleId);
		const tDisabledMessage = getDisabledMessage({
			commissionCycle,
			payPeriod: payPeriod ?? undefined,
			payPeriods: commissionCycle?.payPeriods ?? [],
			timezone,
		});
		return Boolean(tDisabledMessage);
	}, [payPeriod, timezone]);

	const shouldDisabledPartnerPP = useMemo(() => {
		const commissionCycle = commissionCycles.find((cycle) => cycle._id === partnerPayPeriod?.cycleId);
		const tDisabledMessage = getDisabledMessage({
			commissionCycle,
			payPeriod: partnerPayPeriod ?? undefined,
			payPeriods: commissionCycle?.payPeriods ?? [],
			timezone,
		});
		return Boolean(tDisabledMessage);
	}, [partnerPayPeriod, timezone]);

	const currentPayPeriod = {
		payPeriod,
		commissionCycleId,
		disabledPositions: currentDisabledPositions,
		shouldDisabled: shouldDisabledCurrentPP,
	};
	const partnerPayPeriodInfo = {
		payPeriod: partnerPayPeriod,
		disabledPositions: partnerDisabledPositions,
		commissionCycleId: partnerPayPeriod?.cycleId,
		shouldDisabled: shouldDisabledPartnerPP,
	};

	return (
		<PositionsDialog
			open={openPositionsSelector}
			setOpenPositionsSelector={setOpenPositionsSelector}
			closePositionsSelector={handleClosePositionsSelector}
			currentPayPeriod={currentPayPeriod}
			partnerPayPeriod={partnerPayPeriodInfo}
			action={reviewPositions}
			isUsingCallback={true}
			layoutDialogProps={{ title: "Review Positions", btnConfirmText: "Review" }}
		></PositionsDialog>
	);
};

export default RequestReviewDialog;
